'use client'

import { reportHandledException } from '@/lib/error-reporting'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import {
  Alert,
  Button,
  KiaoModal,
  ReceiptRows,
  SettingRow,
  Stepper,
  useKiaoConfirm,
  toast,
} from '@kiao-voice/ui-kit'
import { updateTeamSeats } from './api-client'
import {
  formatTwd,
  MAX_TEAM_SEATS,
  resolveSeatAdjustment,
  type SeatAdjustment,
  type SeatCopy,
  type SubscriptionCycle,
} from './seat-adjustment'

/** 預覽與提醒句的粗體由 i18n 字串裡的 `<b>` 承載，數字先格式化再插值。 */
function SeatCopyText({ copy }: { copy: SeatCopy }) {
  const { t } = useTranslation('web')
  const values: Record<string, string | number> = { ...copy.params }
  for (const key of ['full', 'charge'] as const) {
    if (typeof values[key] === 'number') values[key] = formatTwd(values[key])
  }
  return <Trans t={t} i18nKey={copy.key} values={values} components={{ b: <b /> }} />
}

export interface SeatSectionProps {
  /** 已付費席次（`subscription.seats`）——席次的唯一真相，不從成員列數推。 */
  seats: number
  pendingSeats: number | null
  /** 使用中＋邀請中（`seatUsage.used`）。 */
  used: number
  /** 尚可邀請的席位（`seatUsage.available`）。 */
  available: number
  /** 每席每 cycle 的鎖定價；`null` ＝ 不顯示任何金額。 */
  perSeatPriceTwd: number | null
  cycle: SubscriptionCycle
  periodStart: string | null
  periodEnd: string | null
  /** 本期結束後不再續訂；收據的「下次續訂起」列要改口。 */
  cancelAtPeriodEnd: boolean
  /** 3DS 轉跳 seam（測試注入）。 */
  redirect?: (url: string) => void
  onDone: () => void
  onError: (cause: unknown) => void
}

/**
 * `01 席次`：本期席次與調整席次兩列。
 *
 * 加席會立刻扣款，所以確認介面是一張收據（`<ReceiptRows>`），把「整月原價 →
 * 扣掉沒用到的天數 → 本期應付」三個數字攤開；減席與取消排程不動錢，沿用
 * `useKiaoConfirm()` 的一行確認。成功 toast 的金額用 API 回的 `chargedTwd`，
 * 前端算出來的數字只用於送出前的預覽。
 */
export function SeatSection({
  seats,
  pendingSeats,
  used,
  available,
  perSeatPriceTwd,
  cycle,
  periodStart,
  periodEnd,
  cancelAtPeriodEnd,
  redirect,
  onDone,
  onError,
}: SeatSectionProps) {
  const { t } = useTranslation('web')
  const confirm = useKiaoConfirm()
  const scheduledSeats = pendingSeats ?? seats
  const scheduledSeatsKey = `${seats}:${pendingSeats ?? 'none'}`
  const previousScheduledSeatsKey = useRef(scheduledSeatsKey)
  const [target, setTarget] = useState(scheduledSeats)
  const [busy, setBusy] = useState(false)
  const [receiptOpen, setReceiptOpen] = useState(false)
  // 下界提醒只在使用者動過 stepper 之後才出現：滿席時初始值就等於下界，
  // 一進頁面就掛著「不能再低」會被讀成錯誤，而使用者什麼都還沒做。
  const [touched, setTouched] = useState(false)

  useEffect(() => {
    // 目標席次已由 props 推導過一次；只有伺服器真的給了新的排程才覆寫，
    // 否則 paint 後的重設會吃掉使用者剛打的第一個值。
    if (previousScheduledSeatsKey.current === scheduledSeatsKey) return
    previousScheduledSeatsKey.current = scheduledSeatsKey
    setTarget(scheduledSeats)
    setTouched(false)
  }, [scheduledSeats, scheduledSeatsKey])

  const adjustment: SeatAdjustment = useMemo(
    () =>
      resolveSeatAdjustment({
        seats,
        pendingSeats,
        used,
        target,
        perSeatPriceTwd,
        cycle,
        periodStart,
        periodEnd,
        cancelAtPeriodEnd,
        now: Date.now(),
      }),
    [
      seats,
      pendingSeats,
      used,
      target,
      perSeatPriceTwd,
      cycle,
      periodStart,
      periodEnd,
      cancelAtPeriodEnd,
    ]
  )

  // 收據 modal 只在 `increase` 掛載，但 `receiptOpen` 是獨立的 state：方向離開
  // increase 時 modal 被卸載而 state 仍是 true，下次方向再回到 increase（撥一下
  // stepper）付款 modal 就會自己跳出來，一個 Enter 就扣款。方向或目標席次一變就
  // 關掉它——modal 只能由 `submit()` 打開。
  useEffect(() => {
    setReceiptOpen(false)
  }, [adjustment.direction, adjustment.target])

  const hasFree = available > 0
  const unitLabel =
    perSeatPriceTwd === null
      ? t('team.seatUnit')
      : t(cycle === 'yearly' ? 'team.seatUnitYearly' : 'team.seatUnitMonthly', {
          price: formatTwd(perSeatPriceTwd),
        })

  const applySeats = useCallback(
    async (next: number) => {
      setBusy(true)
      try {
        const result = await updateTeamSeats(next)
        if (result.requires3ds && result.paymentUrl) {
          // 3DS 轉跳走 API 自己寫的 return URL（帶 billingProduct=team_seat_addon），
          // 前端不需要另外寫 intent——與滿席邀請的加席流程同一條路。
          if (redirect) redirect(result.paymentUrl)
          else if (typeof window !== 'undefined') window.location.href = result.paymentUrl
          return
        }
        const added = next - seats
        toast.success(
          typeof result.chargedTwd === 'number'
            ? t('team.seatAddedToast', { seats: added, amount: formatTwd(result.chargedTwd) })
            : t('team.seatAddedToastNoAmount', { seats: added })
        )
        onDone()
      } catch (cause) {
        reportHandledException(cause, { source: 'features/team/seat-section.tsx' })
        onError(cause)
      } finally {
        setBusy(false)
      }
    },
    [onDone, onError, redirect, seats, t]
  )

  const scheduleSeats = useCallback(
    async (next: number, cancelsDecrease: boolean) => {
      const ok = await confirm(
        cancelsDecrease
          ? {
              title: t('team.seatCancelTitle'),
              message: t('team.seatCancelBody', { seats }),
              confirmText: t('team.seatCancelConfirm', { seats }),
              cancelText: t('common:cancel'),
            }
          : {
              title: t('team.seatDecreaseTitle'),
              message: t('team.seatDecreaseBody', { current: seats, next }),
              confirmText: t('team.seatDecreaseConfirm'),
              cancelText: t('common:cancel'),
            }
      )
      if (!ok) return
      setBusy(true)
      try {
        await updateTeamSeats(next)
        toast.success(
          cancelsDecrease ? t('team.seatDecreaseCanceled') : t('team.seatDecreaseScheduled')
        )
        onDone()
      } catch (cause) {
        reportHandledException(cause, { source: 'features/team/seat-section.tsx' })
        onError(cause)
      } finally {
        setBusy(false)
      }
    },
    [confirm, onDone, onError, seats, t]
  )

  const submit = () => {
    if (busy || adjustment.direction === 'noop') return
    if (adjustment.direction === 'increase') {
      setReceiptOpen(true)
      return
    }
    void scheduleSeats(adjustment.target, adjustment.direction === 'cancel')
  }

  const seatNoteKey =
    perSeatPriceTwd === null
      ? 'team.seatNoteFullNoPrice'
      : cycle === 'yearly'
        ? 'team.seatNoteFullYearly'
        : 'team.seatNoteFullMonthly'

  return (
    <>
      <SettingRow
        label={t('team.seatSummaryCurrent')}
        description={
          <>
            {hasFree
              ? t('team.seatRowDescAvailable', { used, free: available })
              : t('team.seatRowDescFull', { used })}
            {pendingSeats !== null ? (
              <span className="block">
                {t('team.seatNextRenewalScheduled', { seats: pendingSeats })}
              </span>
            ) : null}
          </>
        }
        control={
          <span className="flex items-baseline gap-[var(--gap-xs)]">
            <span
              className="font-display text-2xl tabular-nums leading-none"
              aria-label={t('team.seatUsageAria', { used, total: seats })}
            >
              <span data-testid="seat-used">{used}</span>
              <span className="px-0.5 text-muted">/</span>
              <span data-testid="seat-total">{seats}</span>
            </span>
            <span className="text-xs text-muted">{unitLabel}</span>
          </span>
        }
        supportingContent={
          hasFree ? undefined : (
            <p className="text-sm text-muted" data-testid="seat-note-full">
              {perSeatPriceTwd === null
                ? t(seatNoteKey)
                : t(seatNoteKey, { price: formatTwd(perSeatPriceTwd) })}
            </p>
          )
        }
      />

      <SettingRow
        label={t('team.seatAdjustLabel')}
        description={t('team.seatAdjustDesc')}
        control={
          <span className="flex items-center gap-[var(--gap-sm)]" data-testid="seat-adjust-control">
            <Stepper
              editable
              inputLabel={t('team.seatAdjustInputLabel')}
              value={target}
              min={used}
              max={MAX_TEAM_SEATS}
              decrementLabel={t('team.removeSeatAria')}
              incrementLabel={t('team.addSeatAria')}
              onChange={(next) => {
                setTouched(true)
                setTarget(next)
              }}
            />
            <Button
              variant="secondary"
              size="control"
              disabled={busy || adjustment.direction === 'noop'}
              onClick={submit}
            >
              {t(adjustment.ctaKey)}
            </Button>
          </span>
        }
        supportingContent={
          adjustment.preview || (touched && adjustment.atFloor) ? (
            <div className="flex flex-col gap-[var(--gap-xs)]">
              {adjustment.preview ? (
                <p className="text-sm text-muted" data-testid="seat-adjust-preview">
                  <SeatCopyText copy={adjustment.preview} />
                  {adjustment.pendingCancelNotice ? (
                    <>
                      {' '}
                      <SeatCopyText copy={adjustment.pendingCancelNotice} />
                    </>
                  ) : null}
                  {adjustment.noRenewalNotice ? (
                    <>
                      {' '}
                      <SeatCopyText copy={adjustment.noRenewalNotice} />
                    </>
                  ) : null}
                </p>
              ) : null}
              {touched && adjustment.atFloor ? (
                <Alert tone="warn" data-testid="seat-floor-notice">
                  {t('team.seatFloorNotice', { used })}
                </Alert>
              ) : null}
            </div>
          ) : undefined
        }
      />

      {adjustment.direction === 'increase' ? (
        <SeatReceiptModal
          open={receiptOpen}
          busy={busy}
          seats={seats}
          adjustment={adjustment}
          cycle={cycle}
          perSeatPriceTwd={perSeatPriceTwd}
          onClose={() => setReceiptOpen(false)}
          onConfirm={() => {
            setReceiptOpen(false)
            void applySeats(adjustment.target)
          }}
        />
      ) : null}
    </>
  )
}

function SeatReceiptModal({
  open,
  busy,
  seats,
  adjustment,
  cycle,
  perSeatPriceTwd,
  onClose,
  onConfirm,
}: {
  open: boolean
  busy: boolean
  seats: number
  adjustment: SeatAdjustment
  cycle: SubscriptionCycle
  perSeatPriceTwd: number | null
  onClose: () => void
  onConfirm: () => void
}) {
  const { t } = useTranslation('web')
  const amount = adjustment.amount
  const added = adjustment.target - seats
  const yearly = cycle === 'yearly'

  // 「下次續訂起」永遠有一列：席數是這一列的重點（尤其在加席會清掉已排定減席時），
  // 缺鎖定價只省掉金額那一半，不是整列消失。
  const renewalNotice = adjustment.pendingCancelNotice
  const summaryRows = [
    {
      key: 'seats',
      label: t('team.seatProrateSeatsLabel'),
      value: t('team.seatProrateSeatsValue', { from: seats, to: adjustment.target, added }),
      'data-testid': 'seat-receipt-seats',
    },
    {
      key: 'renewal',
      label: renewalNotice ? (
        <>
          {t('team.seatProrateRenewalLabel')}
          <span className="mt-0.5 block text-xs" data-testid="seat-receipt-renewal-pending">
            <SeatCopyText copy={renewalNotice} />
          </span>
        </>
      ) : (
        t('team.seatProrateRenewalLabel')
      ),
      value: adjustment.noRenewalNotice
        ? t('team.seatProrateNoRenewal', {
            seats: adjustment.target,
            date: String(adjustment.noRenewalNotice.params.date),
          })
        : perSeatPriceTwd === null
          ? t('team.seatProrateRenewalSeatsOnly', { seats: adjustment.target })
          : t(yearly ? 'team.seatProrateRenewalYearly' : 'team.seatProrateRenewalMonthly', {
              seats: adjustment.target,
              amount: formatTwd(perSeatPriceTwd * adjustment.target),
            }),
      'data-testid': 'seat-receipt-renewal',
    },
  ]

  return (
    <KiaoModal
      open={open}
      onClose={onClose}
      title={t('team.seatProrateTitle')}
      subtitle={t('team.seatProrateSubtitle')}
      closeLabel={t('common:dismiss')}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            {t('common:cancel')}
          </Button>
          {/* 算不出本期金額就不能讓人按下去：那等於授權一筆沒被報價的扣款。 */}
          <Button variant="primary" onClick={onConfirm} disabled={busy || amount === null}>
            {t('team.seatProrateConfirmCta')}
          </Button>
        </>
      }
    >
      <ReceiptRows rows={summaryRows} data-testid="seat-receipt-summary" />
      {amount ? (
        <>
          <p className="mt-[var(--gap-md)] mb-[var(--gap-xs)] text-xs text-muted">
            {t('team.seatProrateCaption')}
          </p>
          <ReceiptRows
            data-testid="seat-receipt-breakdown"
            rows={[
              {
                key: 'full',
                label: t(
                  yearly ? 'team.seatProrateFullLabelYearly' : 'team.seatProrateFullLabelMonthly',
                  { added: amount.added }
                ),
                value: t('team.seatProrateAmount', { amount: formatTwd(amount.full) }),
                'data-testid': 'seat-receipt-full',
              },
              {
                key: 'discount',
                label: amount.daysKnown
                  ? t('team.seatProrateDiscountLabel', {
                      daysUsed: amount.daysUsed,
                      daysLeft: amount.daysLeft,
                      daysTotal: amount.daysTotal,
                    })
                  : t('team.seatProrateDiscountLabelNoDays'),
                value: t('team.seatProrateDiscountValue', { amount: formatTwd(amount.discount) }),
                variant: 'discount' as const,
                'data-testid': 'seat-receipt-discount',
              },
              {
                key: 'total',
                label: t('team.seatProrateTotalLabel'),
                value: t('team.seatProrateAmount', { amount: formatTwd(amount.charge) }),
                variant: 'total' as const,
                'data-testid': 'seat-receipt-total',
              },
            ]}
          />
        </>
      ) : (
        <Alert tone="warn" className="mt-[var(--gap-md)]" data-testid="seat-receipt-no-amount">
          {t('team.seatProrateNoAmountWarning')}
        </Alert>
      )}
    </KiaoModal>
  )
}
